import Papa from 'papaparse';

// Load and parse CSV file
export const loadCSV = async (filePath, cacheBust = false) => {
  try {
    // Add cache-busting parameter if requested
    const url = cacheBust ? `${filePath}?t=${Date.now()}` : filePath;
    const response = await fetch(url, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    const text = await response.text();
    return new Promise((resolve, reject) => {
      // Detect delimiter - check if semicolon is used (for Asset Model List)
      const delimiter = text.includes(';') ? ';' : ',';
      
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        delimiter: delimiter,
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error(`Error loading CSV from ${filePath}:`, error);
    throw error;
  }
};

// Parse standard consumption rate from Asset Model List
export const parseStandardRate = (rateString) => {
  if (!rateString || rateString.trim() === '') return null;
  
  // Handle formats like "6.95 L/HOURS" or "9.45 KMS/L" or "0.82 KMS/L| 7.25 L/HOURS"
  const parts = rateString.split('|').map(p => p.trim());
  
  let kmsPerLtr = null;
  let ltrPerHour = null;
  
  parts.forEach(part => {
    const match = part.match(/([\d.]+)\s*(KMS\/L|L\/HOURS|LTR\/HOURS|KWH\/L)/i);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2].toUpperCase();
      if (unit.includes('KMS') || unit.includes('KWH')) {
        kmsPerLtr = value;
      } else if (unit.includes('L/HOURS') || unit.includes('LTR/HOURS')) {
        ltrPerHour = value;
      }
    }
  });
  
  return { kmsPerLtr, ltrPerHour };
};

// Determine consumption rate unit based on asset type and available data
export const determineConsumptionRateUnit = (assetType, distanceTravelled, hasStandardKms, hasStandardLtr) => {
  // If it's a vehicle (truck, pickup) and has distance, use KMS/LTR
  if ((assetType.includes('Truck') || assetType.includes('Pick up') || assetType.includes('Tanker')) && distanceTravelled > 0) {
    return 'KMS/LTR';
  }
  
  // If standard rate is in KMS/LTR format, use that
  if (hasStandardKms) {
    return 'KMS/LTR';
  }
  
  // If standard rate is in LTR/HOURS format, use that
  if (hasStandardLtr) {
    return 'LTR/HOURS';
  }
  
  // Default based on asset type
  if (assetType.includes('Compressor') || assetType.includes('Generator') || assetType.includes('DG') || 
      assetType.includes('Excavator') || assetType.includes('Loader') || assetType.includes('Dozer')) {
    return 'LTR/HOURS';
  }
  
  return 'KMS/LTR'; // Default
};

// Calculate consumption variance
export const calculateConsumptionVariance = (actualRate, stdRate, unit) => {
  if (!actualRate || !stdRate || actualRate === 0 || stdRate === 0) return null;
  
  const variance = ((actualRate - stdRate) / stdRate) * 100;
  return variance;
};

// Calculate idling percentage
export const calculateIdlingPercentage = (idleTimeMin, ignitionTimeMin) => {
  if (!ignitionTimeMin || ignitionTimeMin === 0) return 0;
  return (idleTimeMin / ignitionTimeMin) * 100;
};

// Format running hours from minutes
export const formatRunningHours = (ignitionTimeMin) => {
  if (!ignitionTimeMin) return '00:00:00';
  const hours = Math.floor(ignitionTimeMin / 60);
  const minutes = Math.floor(ignitionTimeMin % 60);
  const seconds = Math.floor((ignitionTimeMin % 1) * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Format moving time from minutes (HH:MM format)
export const formatMovingTime = (movingTimeMin) => {
  if (!movingTimeMin || movingTimeMin < 0) return '00:00';
  const hours = Math.floor(movingTimeMin / 60);
  const minutes = Math.floor(movingTimeMin % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Determine issues based on data
export const determineIssues = (data, unit, stdRate, actualRate) => {
  const issues = [];
  
  // High consumption variance
  if (data.consumptionVariance !== null && data.consumptionVariance !== undefined) {
    if (unit === 'KMS/LTR' || unit === 'KWH/L') {
      // For KMS/LTR: negative variance is bad (worse efficiency)
      if (data.consumptionVariance < -10) {
        issues.push('highConsumptionVariance');
      } else if (data.consumptionVariance > 15) {
        issues.push('greenConsumptionRate');
      }
    } else if (unit === 'LTR/HOURS') {
      // For LTR/HOURS: positive variance is bad (higher consumption)
      if (data.consumptionVariance > 10) {
        issues.push('highConsumptionVariance');
      } else if (data.consumptionVariance < -15) {
        issues.push('greenConsumptionRate');
      }
    }
  }
  
  // High refuel difference
  if (data.refuelDifference && Math.abs(data.refuelDifference) > 5) {
    issues.push('highRefuelDifference');
  }
  
  // High idling
  if (data.idlingPercentage > 5) {
    issues.push('highIdling');
  }
  
  // Possible drain
  if (data.possibleDrain > 0) {
    issues.push('possibleDrain');
  }
  
  // Data gap 24+ hours
  if (data.hasDataGap24Plus) {
    issues.push('dataGap24Plus');
  }
  
  // Low data completeness (< 95%)
  if (data.dataCompleteness !== null && data.dataCompleteness !== undefined && data.dataCompleteness < 95) {
    issues.push('lowDataCompleteness');
  }
  
  return issues;
};

// Determine priority based on issues
export const determinePriority = (issues) => {
  if (issues.includes('possibleDrain') || 
      (issues.includes('highConsumptionVariance') && issues.includes('highRefuelDifference'))) {
    return 'critical';
  }
  if (issues.includes('highConsumptionVariance') || 
      issues.includes('highRefuelDifference') || 
      issues.includes('dataGap24Plus') ||
      issues.includes('lowDataCompleteness')) {
    return 'high';
  }
  if (issues.includes('greenConsumptionRate')) {
    return 'low';
  }
  return 'normal';
};

